(() => {
  'use strict';
  if (customElements.get('fab-audit-kit')) return;
  const workshopStates = new Map();

  // Net product mass is read only from an explicit g/kg value; vessel ml and wick cm are not mass.
  const netGrams = title => {
    const matches = Array.from(String(title || '').matchAll(/(\d+(?:[.,]\d+)?)\s*(kg|kilogram|gram|gr|g)(?=$|[^a-z])/gi));
    if (!matches.length) return 0;
    const match = matches[matches.length - 1];
    const amount = Number(match[1].replace(',', '.'));
    return Number.isFinite(amount) && amount > 0 ? amount * (/^(kg|kilogram)$/i.test(match[2]) ? 1000 : 1) : 0;
  };

  // The existing row purchases one real variant; compare its total sufficient package cost.
  const woodPlan = (required, options) => {
    if (!Number.isInteger(required) || required < 1) return null;
    let best = null;
    for (const option of options) {
      const pack = Number(option.dataset.packCount);
      const price = Number(option.dataset.price);
      const min = Number(option.dataset.min) || 1;
      const step = Number(option.dataset.step) || 1;
      const max = Math.min(Number(option.dataset.max) || 500, 500);
      if (option.disabled || option.dataset.standIncluded !== 'true' || !Number.isInteger(pack) || pack < 1 || !Number.isInteger(price) || price < 0 || !Number.isInteger(min) || min < 1 || !Number.isInteger(step) || step < 1) continue;
      const raw = Math.max(min, Math.ceil(required / pack));
      const quantity = min + Math.ceil((raw - min) / step) * step;
      if (quantity > max) continue;
      const candidate = { id: option.value, quantity, pack, count: pack * quantity, cost: price * quantity };
      if (!best || candidate.cost < best.cost || candidate.cost === best.cost && (candidate.count < best.count || candidate.count === best.count && candidate.quantity < best.quantity)) best = candidate;
    }
    return best;
  };

  class Kit extends HTMLElement {
    connectedCallback() {
      if (this.life) return;
      this.life = new AbortController();
      const signal = this.life.signal;
      this.form = this.querySelector('form');
      this.owner = this.closest('product-info');
      this.disclosure = this.querySelector('[data-kit-disclosure]');
      this.stateKey = `${this.dataset.mainHandle || window.location.pathname}:${this.closest('[data-fab-workshop]')?.id || ''}`;
      this.busy = false;
      this.lastAdded = '';
      this.native = () => this.owner?.querySelector('.buy-buttons-block form[data-type="add-to-cart-form"]');
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'tr', { style: 'currency', currency: this.dataset.currency || 'TRY' });
      try { this.variants = JSON.parse(this.querySelector('[data-kit-variants]')?.textContent || '[]'); }
      catch (_) { this.variants = []; }
      try { this.strings = JSON.parse(this.querySelector('[data-kit-strings]')?.textContent || '{}'); }
      catch (_) { this.strings = {}; }
      const restored = this.restoreState();
      this.addEventListener('change', event => { this.rememberManual(event.target); this.update(); }, { signal });
      this.addEventListener('input', event => {
        if (event.target.matches('[data-kit-qty], [data-kit-wax-kg], [data-kit-fill-g]')) {
          this.rememberManual(event.target);
          this.update();
        }
      }, { signal });
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-kit-delta]');
        if (!button || this.busy) return;
        const input = button.closest('[data-kit-row]').querySelector('[data-kit-qty]');
        if (input.disabled) return;
        const step = Number(input.step) || 1;
        const min = Number(input.min) || 1;
        const max = Math.min(Number(input.max) || 500, 500);
        input.value = String(Math.max(min, Math.min(max, (Number(input.value) || min) + Number(button.dataset.kitDelta) * step)));
        this.rememberManual(input);
        this.update();
      }, { signal });
      this.form.addEventListener('submit', event => this.submit(event), { signal });
      this.owner?.addEventListener('change', () => this.update(), { signal });
      this.owner?.addEventListener('input', event => { if (event.target.name === 'quantity') this.update(); }, { signal });
      for (const name of ['variant:update', 'product:updated', 'product-info:loaded']) document.addEventListener(name, () => this.update(), { signal });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') this.unsub = subscribe(PUB_SUB_EVENTS.variantChange, () => this.update());
      this.observer = new MutationObserver(() => this.update());
      const native = this.native();
      if (native) this.observer.observe(native, { childList: true, subtree: true, attributes: true, attributeFilter: ['value', 'disabled'] });
      this.disclosure?.addEventListener('toggle', () => this.updateToggle(), { signal });
      window.addEventListener('hashchange', () => this.openForHash(), { signal });
      document.addEventListener('click', event => {
        const link = event.target.closest('a[href]');
        if (!link) return;
        let url;
        try { url = new URL(link.href, window.location.href); } catch (_) { return; }
        if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || !this.matchesHash(url.hash)) return;
        this.disclosure.open = true;
        this.updateToggle();
      }, { signal });
      this.update();
      this.updateToggle();
      if (!restored) this.openForHash();
    }

    disconnectedCallback() {
      this.saveState();
      if (this.state?.active === this) this.state.active = null;
      this.life?.abort();
      this.life = null;
      this.observer?.disconnect();
      this.unsub?.();
    }

    saveState() {
      if (!this.stateKey || this.state?.active && this.state.active !== this) return;
      const state = this.state || workshopStates.get(this.stateKey) || {};
      state.open = !!this.disclosure?.open;
      state.includeMain = this.querySelector('[data-kit-main]')?.checked;
      state.extras = Array.from(this.querySelectorAll('[data-kit-row]'), row => ({
        handle: row.dataset.handle,
        id: row.querySelector('select')?.value || row.querySelector('select')?.selectedOptions[0]?.value,
        quantity: row.querySelector('[data-kit-qty]')?.value,
        checked: !!row.querySelector('[data-kit-check]')?.checked,
        manual: row.dataset.kitManual === 'true',
      }));
      state.fillGrams = this.querySelector('[data-kit-fill-g]')?.value;
      state.waxKg = this.querySelector('[data-kit-wax-kg]')?.value;
      state.lastAdded = this.lastAdded || state.lastAdded || '';
      state.active = this;
      this.state = state;
      workshopStates.set(this.stateKey, state);
    }

    restoreState() {
      const state = workshopStates.get(this.stateKey);
      if (!state) return false;
      this.state = state;
      state.active = this;
      this.lastAdded = state.lastAdded || '';
      if (this.disclosure) this.disclosure.open = !!state.open;
      const include = this.querySelector('[data-kit-main]');
      if (include && typeof state.includeMain === 'boolean') include.checked = state.includeMain;
      if (state.fillGrams !== undefined && this.querySelector('[data-kit-fill-g]')) this.querySelector('[data-kit-fill-g]').value = state.fillGrams;
      if (state.waxKg !== undefined && this.querySelector('[data-kit-wax-kg]')) this.querySelector('[data-kit-wax-kg]').value = state.waxKg;
      for (const row of this.querySelectorAll('[data-kit-row]')) {
        const saved = state.extras?.find(extra => extra.handle === row.dataset.handle);
        if (!saved) continue;
        const select = row.querySelector('select');
        const option = Array.from(select?.options || []).find(option => option.value === saved.id);
        const check = row.querySelector('[data-kit-check]');
        if (option) select.value = saved.id;
        check.checked = !!option && !option.disabled && saved.checked;
        if (saved.quantity !== undefined) row.querySelector('[data-kit-qty]').value = saved.quantity;
        row.dataset.kitManual = saved.manual ? 'true' : 'false';
      }
      return true;
    }

    text(selector, value) {
      const element = this.querySelector(selector);
      if (element && element.textContent !== value) element.textContent = value;
    }

    matchesHash(hash) {
      const id = this.closest('[data-fab-workshop]')?.id;
      try { return !!id && decodeURIComponent(hash.slice(1)) === id; }
      catch (_) { return false; }
    }

    openForHash() {
      if (!this.disclosure || !this.matchesHash(window.location.hash)) return;
      this.disclosure.open = true;
      this.updateToggle();
      this.closest('[data-fab-workshop]')?.scrollIntoView({ block: 'start' });
    }

    updateToggle() {
      this.text('[data-kit-toggle-label]', this.disclosure?.open ? this.strings.collapse : this.strings.expand);
      this.saveState();
    }

    rememberManual(target) {
      const row = target.closest('[data-kit-row]');
      if (row && target.matches('select, [data-kit-qty]')) {
        row.dataset.kitManual = 'true';
        if (row.hasAttribute('data-kit-gel-wax')) {
          const kg = this.querySelector('[data-kit-wax-kg]');
          const option = row.querySelector('select')?.selectedOptions[0];
          const grams = netGrams(option?.textContent) || netGrams(row.dataset.productTitle);
          const quantity = Number(row.querySelector('[data-kit-qty]')?.value);
          if (kg && grams > 0 && Number.isInteger(quantity) && quantity > 0) kg.value = String(grams * quantity / 1000);
        }
      } else if (target.matches('[data-kit-wax-kg]')) {
        // Editing the project amount is an explicit request to refresh the guide's wax quantity.
        const wax = this.querySelector('[data-kit-gel-wax]');
        if (wax) wax.dataset.kitManual = 'false';
      }
    }

    updateGelPlan(variant, quantity) {
      if (this.dataset.kitContext !== 'gel') return true;
      const kg = this.querySelector('[data-kit-wax-kg]');
      const fill = Number(this.querySelector('[data-kit-fill-g]')?.value);
      const grams = kg ? Number(kg.value) * 1000 : (netGrams(variant?.title) || netGrams(this.dataset.mainTitle)) * quantity;
      const valid = Number.isFinite(grams) && grams > 0 && grams <= 50000000 && Number.isInteger(fill) && fill >= 1 && fill <= 10000;
      if (!valid) {
        this.text('[data-kit-gel-summary]', this.strings.gel_plan_unavailable || '');
        this.text('[data-kit-gel-feedback]', '');
        return false;
      }
      const number = new Intl.NumberFormat(document.documentElement.lang || 'tr', { maximumFractionDigits: 3 });
      const required = Math.ceil(grams / fill);
      this.text('[data-kit-gel-summary]', (this.strings.gel_plan_summary || '').replace('__WAX__', number.format(grams / 1000)).replace('__FILL__', number.format(fill)).replace('__COUNT__', number.format(required)));
      const wax = this.querySelector('[data-kit-gel-wax]');
      if (kg && wax && wax.dataset.kitManual !== 'true') {
        const option = wax.querySelector('select')?.selectedOptions[0];
        const net = netGrams(option?.textContent) || netGrams(wax.dataset.productTitle);
        const min = Number(option?.dataset.min) || 1;
        const step = Number(option?.dataset.step) || 1;
        const max = Math.min(Number(option?.dataset.max) || 500, 500);
        if (net > 0) {
          const raw = Math.max(min, Math.ceil(grams / net));
          const count = min + Math.ceil((raw - min) / step) * step;
          const input = wax.querySelector('[data-kit-qty]');
          // Do not silently under-fill a large project when the real quantity rule is exceeded.
          input.value = String(count);
          if (count > max && wax.querySelector('[data-kit-check]')?.checked) return false;
        }
      }
      const wood = this.querySelector('[data-kit-gel-wood]');
      const select = wood?.querySelector('select');
      if (!wood || !select) {
        this.text('[data-kit-gel-feedback]', this.strings.gel_plan_unavailable || '');
        return true;
      }
      if (wood.dataset.kitManual === 'true') {
        this.text('[data-kit-gel-feedback]', this.strings.gel_plan_manual || '');
        return true;
      }
      const plan = woodPlan(required, Array.from(select.options));
      if (!plan) {
        this.text('[data-kit-gel-feedback]', this.strings.gel_plan_unavailable || '');
        return !wood.querySelector('[data-kit-check]')?.checked;
      }
      select.value = plan.id;
      wood.querySelector('[data-kit-qty]').value = String(plan.quantity);
      this.text('[data-kit-gel-feedback]', (this.strings.gel_plan_package || '').replace('__QUANTITY__', number.format(plan.quantity)).replace('__PACKAGE__', select.selectedOptions[0]?.textContent.trim() || ''));
      return true;
    }

    update() {
      if (this.busy) return;
      let total = 0;
      let count = 0;
      let invalid = false;
      this.items = [];
      const include = this.querySelector('[data-kit-main]');
      const native = this.native();
      let mainVariant = null;
      let mainQuantity = 1;
      if (include) {
        const data = native ? new FormData(native) : null;
        const id = String(data?.get('id') || '');
        const variant = this.variants.find(item => String(item.id) === id);
        const quantity = Number(data?.get('quantity') || 1);
        mainVariant = variant;
        mainQuantity = quantity;
        const input = this.owner?.querySelector('.product-form__quantity input[name="quantity"]') || native?.querySelector('[name="quantity"]');
        const min = Number(input?.min || variant?.min) || 1;
        const step = Number(input?.step || variant?.step) || 1;
        const max = Math.min(Number(input?.max || variant?.max) || 500, 500);
        const valid = variant?.available && Number.isInteger(quantity) && quantity >= min && quantity <= max && (quantity - min) % step === 0 && !native?.querySelector('[name="id"]')?.disabled;
        this.text('[data-kit-main-summary]', valid ? `${quantity} × ${variant.title} · ${this.money.format(variant.price * quantity / 100)}` : this.strings.invalid_main);
        if (include.checked) {
          if (valid) {
            total += variant.price * quantity;
            this.items.push({ id, quantity, price: variant.price, handle: this.dataset.mainHandle });
          } else invalid = true;
        }
      }
      const mainUnit = this.querySelector('[data-kit-main-unit-price]');
      if (mainUnit) {
        const unit = mainVariant?.fragranceUnitPriceMinor;
        mainUnit.hidden = unit == null || !Number.isFinite(Number(unit));
        mainUnit.textContent = mainUnit.hidden ? '' : (this.dataset.fragranceUnitPrice || '__PRICE__ / 1000 ml').replace('__PRICE__',this.money.format(Number(unit) / 100));
      }
      if (!this.updateGelPlan(mainVariant, mainQuantity)) invalid = true;
      for (const row of this.querySelectorAll('[data-kit-row]')) {
        const select = row.querySelector('select');
        const option = select?.selectedOptions[0];
        const input = row.querySelector('[data-kit-qty]');
        const check = row.querySelector('[data-kit-check]');
        const min = Number(option?.dataset.min) || 1;
        const step = Number(option?.dataset.step) || 1;
        const max = Math.min(Number(option?.dataset.max) || 500, 500);
        input.min = String(min);
        input.step = String(step);
        input.max = String(max);
        check.disabled = !option || option.disabled;
        if (check.disabled) check.checked = false;
        input.disabled = !check.checked;
        row.querySelectorAll('[data-kit-delta]').forEach(button => { button.disabled = input.disabled; });
        const quantity = Number(input.value);
        const valid = Number.isInteger(quantity) && quantity >= min && quantity <= max && (quantity - min) % step === 0;
        const price = Number(option?.dataset.price) || 0;
        const priceText = this.money.format(price * (valid ? quantity : 1) / 100);
        if (row.querySelector('[data-kit-price]').textContent !== priceText) row.querySelector('[data-kit-price]').textContent = priceText;
        const unitNode = row.querySelector('[data-kit-unit-price]');
        if (unitNode) {
          const unit = String(option?.dataset.unitPrice ?? '').trim();
          unitNode.hidden = unit === '' || !Number.isFinite(Number(unit));
          unitNode.textContent = unitNode.hidden ? '' : (row.dataset.fragranceUnitPrice || '__PRICE__ / 1000 ml').replace('__PRICE__',this.money.format(Number(unit) / 100));
        }
        if (check.checked) {
          if (!valid) invalid = true;
          else {
            count++;
            total += price * quantity;
            this.items.push({ id: option.value, quantity, price, handle: row.dataset.handle });
          }
        }
      }
      this.text('[data-kit-count]', (include?.checked ? this.strings.main_count : this.strings.extra_count)?.replace('__COUNT__', String(count)) || String(count));
      this.text('[data-kit-total]', this.money.format(total / 100));
      this.fingerprint = JSON.stringify(this.items);
      if (this.state?.lastAdded) this.lastAdded = this.state.lastAdded;
      const done = this.fingerprint === this.lastAdded;
      const button = this.querySelector('[data-kit-submit]');
      button.disabled = invalid || !this.items.length || done || !!this.state?.pending;
      button.textContent = this.state?.pending ? this.strings.adding : done ? this.strings.added : include?.checked ? this.strings.add_with_main : this.strings.add_complements;
      if (this.state?.pending) {
        button.setAttribute('aria-busy', 'true');
        this.text('[data-kit-status]', this.strings.adding);
      }
      this.saveState();
    }

    async submit(event) {
      event.preventDefault();
      if (this.busy) return;
      this.update();
      const button = this.querySelector('[data-kit-submit]');
      const status = this.querySelector('[data-kit-status]');
      if (button.disabled || !this.form.reportValidity()) return;
      const native = this.native();
      const rows = this.items.map(item => ({ ...item }));
      const fingerprint = this.fingerprint;
      if (this.querySelector('[data-kit-main]')?.checked) {
        if (!native || !native.reportValidity()) return;
        const data = new FormData(native);
        if (data.get('selling_plan')) {
          status.textContent = this.strings.selling_plan;
          return;
        }
        const properties = {};
        for (const [key, value] of data.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (!match) continue;
          if (typeof value !== 'string') {
            status.textContent = this.strings.file_properties;
            return;
          }
          properties[match[1]] = value;
        }
        rows[0].properties = properties;
      }
      this.busy = true;
      if (this.state) this.state.pending = true;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      status.textContent = this.strings.adding;
      try {
        if (!window.FabrikoneSafeCart) throw Error(this.strings.unavailable_cart);
        await window.FabrikoneSafeCart.add(rows);
        this.lastAdded = fingerprint;
        if (this.state) this.state.lastAdded = fingerprint;
        status.textContent = this.strings.success;
        this.querySelector('[data-kit-cart]').hidden = false;
      } catch (error) {
        status.textContent = error.message || this.strings.failed;
        this.querySelector('[data-kit-cart]').hidden = false;
        if (error.code === 'uncertain') this.lastAdded = fingerprint;
        if (error.code === 'uncertain' && this.state) this.state.lastAdded = fingerprint;
      } finally {
        this.busy = false;
        if (this.state) this.state.pending = false;
        button.removeAttribute('aria-busy');
        const replacement = this.state?.active;
        if (replacement && replacement !== this) {
          replacement.text('[data-kit-status]', status.textContent);
          replacement.querySelector('[data-kit-cart]').hidden = this.querySelector('[data-kit-cart]').hidden;
          replacement.querySelector('[data-kit-submit]').removeAttribute('aria-busy');
          replacement.update();
        }
        this.update();
      }
    }
  }

  customElements.define('fab-audit-kit', Kit);
})();

